import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Loader2, ExternalLink } from 'lucide-react';
import { sectorsApi, brandsApi } from '../services/api';
import RichTextContent from '../components/RichTextContent';

const SectorPage = () => {
  const { sectorId } = useParams();
  const [sector, setSector] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [sectorRes, brandsRes] = await Promise.all([
          sectorsApi.get(sectorId),
          brandsApi.getAll(),
        ]);
        setSector(sectorRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching sector:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [sectorId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!sector) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-gray-900 mb-4">Sector not found</h1>
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Get brand details for selected brands
  const selectedBrandDetails = sector.selectedBrands?.map(sb => {
    const brand = brands.find(b => b.id === sb.brandId);
    return brand ? { ...brand, customUrl: sb.customUrl } : null;
  }).filter(Boolean) || [];

  return (
    <div className="bg-white" data-testid="sector-page">
      {/* Hero Section with 3 Images */}
      <section className="relative">
        {/* Breadcrumb */}
        <div className="absolute top-0 left-0 right-0 z-10 pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center space-x-2 text-sm text-white/80">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-white">Sectors</span>
              <ChevronRight className="h-4 w-4" />
              <span className="text-white">{sector.name}</span>
            </nav>
          </div>
        </div>

        {/* Three Hero Images */}
        <div className="grid grid-cols-1 md:grid-cols-3 min-h-[50vh]">
          {[0, 1, 2].map((index) => {
            const imageUrl = sector.heroImages?.[index];
            const linkUrl = sector.heroImageLinks?.[index];
            const hasLink = linkUrl && linkUrl.trim() !== '';
            
            const imageContent = (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ 
                    backgroundImage: imageUrl 
                      ? `url(${imageUrl})` 
                      : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                  }}
                />
                <div className={`absolute inset-0 bg-black/30 ${hasLink ? 'group-hover:bg-black/20 transition-colors' : ''}`} />
                {hasLink && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="bg-white/90 text-gray-900 px-4 py-2 rounded-full text-sm font-medium">
                      Click to explore
                    </span>
                  </div>
                )}
              </>
            );

            return (
              <div key={index} className="relative h-[300px] md:h-[50vh]">
                {hasLink ? (
                  <a 
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 group cursor-pointer"
                    data-testid={`hero-image-link-${index}`}
                  >
                    {imageContent}
                  </a>
                ) : (
                  imageContent
                )}
              </div>
            );
          })}
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white">
              {sector.title || sector.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {sector.introText ? (
            <RichTextContent 
              content={sector.introText} 
              className="text-xl md:text-2xl text-gray-600 leading-relaxed text-center"
            />
          ) : (
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed text-center">
              Explore our lighting solutions for {sector.name.toLowerCase()} applications.
            </p>
          )}
        </div>
      </section>

      {/* Selected Brands Section */}
      {selectedBrandDetails.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-medium text-gray-900 text-center mb-12">
              Our Partner Brands for {sector.name}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {selectedBrandDetails.map((brand) => {
                const linkUrl = brand.customUrl || `/brand/${brand.id}`;
                const isExternal = brand.customUrl && brand.customUrl.startsWith('http');
                
                return (
                  <div key={brand.id} className="flex flex-col items-center">
                    {isExternal ? (
                      <a
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex flex-col items-center"
                        data-testid={`brand-link-${brand.id}`}
                      >
                        <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow w-full flex items-center justify-center h-24">
                          {brand.logo ? (
                            <img 
                              src={brand.logo} 
                              alt={brand.name}
                              className="max-h-12 max-w-full object-contain group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <span className="text-lg font-medium text-gray-900">{brand.name}</span>
                          )}
                        </div>
                        <span className="mt-3 text-sm text-gray-600 group-hover:text-gray-900 flex items-center">
                          {brand.name}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </span>
                      </a>
                    ) : (
                      <Link
                        to={linkUrl}
                        className="group flex flex-col items-center"
                        data-testid={`brand-link-${brand.id}`}
                      >
                        <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow w-full flex items-center justify-center h-24">
                          {brand.logo ? (
                            <img 
                              src={brand.logo} 
                              alt={brand.name}
                              className="max-h-12 max-w-full object-contain group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <span className="text-lg font-medium text-gray-900">{brand.name}</span>
                          )}
                        </div>
                        <span className="mt-3 text-sm text-gray-600 group-hover:text-gray-900">
                          {brand.name}
                        </span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-medium text-gray-900 mb-4">
            Looking for {sector.name} lighting solutions?
          </h2>
          <p className="text-gray-600 mb-8">
            Get in touch with our team to discuss your project requirements.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
          >
            Contact Us
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SectorPage;
